import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./DACalendar.css";
// This JSX expects a companion CSS file that defines the `.dacx-*` classes.
// We'll share the CSS next.

export default function DACalendar() {
  // Filters + loading state
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersErr, setFiltersErr] = useState("");
  const [dateOptions, setDateOptions] = useState([]); // [{value, display}]
  const [locationGroups, setLocationGroups] = useState([]); // [{group_label, group_options:[{value, display}]}]

  // Controlled form inputs
  const [loc, setLoc] = useState("");
  const [judge, setJudge] = useState("");
  const [d, setD] = useState("");
  const [atty, setAtty] = useState("");

  // Search state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [results, setResults] = useState(null);

  // UI state: 'cards' | 'table'
  const [activeTab, setActiveTab] = useState("cards");

  // Expand/collapse all
  const [expandAll, setExpandAll] = useState(false);

  // A polite aria-live region message when results change
  const [liveMsg, setLiveMsg] = useState("");

  // Defaults once filters load
  const defaultLoc = useMemo(() => {
    if (!locationGroups.length) return "";
    const preferred = "1868D";
    for (const g of locationGroups) {
      const found = g.group_options.find((o) => o.value === preferred);
      if (found) return found.value;
    }
    return locationGroups[0]?.group_options?.[0]?.value || "";
  }, [locationGroups]);

  const defaultDate = useMemo(() => {
    if (!dateOptions.length) return "";
    const todayISO = new Date().toISOString().slice(0, 10);
    const today = dateOptions.find((o) => o.value === todayISO);
    return (today?.value) || dateOptions[0].value;
  }, [dateOptions]);

  // Fetch filters on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setFiltersLoading(true);
        setFiltersErr("");
        const base = process.env.REACT_APP_API_BASE || "http://localhost:5000";
        const res = await fetch(`${base}/calendar/available_filters`);
        if (!res.ok) throw new Error(`Filters HTTP ${res.status}`);
        const data = await res.json();
        setDateOptions(data.date_options || []);
        setLocationGroups(data.location_options || []);
      } catch (e) {
        setFiltersErr(e.message || "Failed to load filter options.");
      } finally {
        setFiltersLoading(false);
      }
    };
    fetchFilters();
  }, []);

  // Initialize defaults when filters are ready
  useEffect(() => {
    if (!filtersLoading && !filtersErr) {
      setLoc((prev) => prev || defaultLoc);
      setD((prev) => prev || defaultDate);
    }
  }, [filtersLoading, filtersErr, defaultLoc, defaultDate]);

  // When results change, announce via aria-live
  useEffect(() => {
    const count = (results?.results_count ?? results?.cases?.length) || 0;
    if (count >= 0) setLiveMsg(`Results updated: ${count} case${count === 1 ? "" : "s"}.`);
  }, [results]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setResults(null);

    const payload = { loc, judge, d, atty };

    try {
      const base = process.env.REACT_APP_API_BASE || "http://localhost:5000";
      const res = await fetch(`${base}/calendar/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResults(data);
      // keep current expand/collapse preference after new searches
    } catch (e2) {
      setErr(e2.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setLoc(defaultLoc);
    setJudge("HRUBY-MILLS");
    setD(defaultDate);
    setAtty("");
  };

  const formDisabled = filtersLoading || !!filtersErr;

  const total = (() => {
    if (!results) return 0;
    if (typeof results.results_count === "number") return results.results_count;
    return results.cases?.length || 0;
  })();

  return (
    <div className="dacx-container">
      <header className="dacx-header">
        <h1>District & Justice Court Calendar Search</h1>
      </header>

      {/* Filters loading / error */}
      {filtersLoading && <FilterSkeleton />}
      {filtersErr && (
        <div className="dacx-alert dacx-alert--error" role="alert">
          Couldn’t load the latest filter options. {filtersErr}
          <div>
            <button className="dacx-button dacx-button--ghost" onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      )}

      {/* Search Form */}
      <form className={`dacx-form ${formDisabled ? "is-disabled" : ""}`} onSubmit={handleSubmit} aria-disabled={formDisabled}>
        <fieldset disabled={formDisabled} style={{ border: "none", padding: 0, margin: 0 }}>
          <div className="dacx-fieldrow">
            <Field label="Location">
              <select id="loc" name="loc" value={loc} onChange={(e) => setLoc(e.target.value)}>
                <option value="" disabled>
                  Please select
                </option>
                {locationGroups.map((g, gi) => (
                  <optgroup key={gi} label={g.group_label}>
                    {g.group_options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.display}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>

            <Field label="Judge">
              <input type="text" name="judge" placeholder="Judge name" value={judge} onChange={(e) => setJudge(e.target.value)} />
            </Field>
          </div>

          <div className="dacx-fieldrow">
            <Field label="Date">
              <select id="d" name="d" value={d} onChange={(e) => setD(e.target.value)}>
                {dateOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.display}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Attorney (optional)">
              <input type="text" name="atty" placeholder="Attorney name" value={atty} onChange={(e) => setAtty(e.target.value)} />
            </Field>
          </div>

          <div className="dacx-actions">
            <div className="dacx-actions__left">
              <button type="submit" className="dacx-button dacx-button--primary" disabled={loading || formDisabled}>
                {loading ? "Loading…" : "View Location Calendar"}
              </button>
              <button type="button" className="dacx-button dacx-button--ghost" onClick={handleReset} disabled={loading || formDisabled}>
                Reset
              </button>
            </div>
            <div className="dacx-actions__note" aria-live="polite">Full Court Calendar Reports are updated weekdays at 5:30am.</div>
          </div>
        </fieldset>
      </form>

      {/* Results Toolbar */}
      <ResultsToolbar
        total={total}
        loc={loc}
        judge={judge}
        date={d}
        atty={atty}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        expandAll={expandAll}
        setExpandAll={setExpandAll}
        results={results}
      />

      {/* Live region for screen readers */}
      <div className="sr-only" aria-live="polite">{liveMsg}</div>

      {/* Content panes */}
      <div className="dacx-tabpanes">
        {err && (
          <div className="dacx-alert dacx-alert--error" role="alert">
            {err}
            <div>
              <button className="dacx-button dacx-button--ghost" onClick={() => setErr("")}>Dismiss</button>
            </div>
          </div>
        )}

        {loading && <ResultsSkeleton view={activeTab} />}

        {!loading && results && total === 0 && (
          <EmptyState onClear={() => handleReset()} />
        )}

        {!loading && results && total > 0 && (
          <div>
            {activeTab === "cards" ? (
              <ResultsCards data={results} expandAll={expandAll} />
            ) : (
              <ResultsTable data={results} expandAll={expandAll} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Components ---------------- */

function ResultsToolbar({ total, loc, judge, date, atty, activeTab, setActiveTab, expandAll, setExpandAll, results }) {
  const chips = [
    loc && { label: "Location", value: loc },
    judge && { label: "Judge", value: judge },
    date && { label: "Date", value: date },
    atty && { label: "Attorney", value: atty },
  ].filter(Boolean);

  const handlePrint = () => window.print();

  const handleExport = () => {
    if (!results?.cases?.length) return;
    const rows = results.cases.map((c) => ({
      time: c.time || "",
      date: c.date || "",
      case_number: (c.case_number || "").replace(/^Case\s*#\s*/i, ""),
      defendant: c.defendant || "",
      plaintiff: c.plaintiff || "",
      hearing_type: c.hearing_type || "",
      conference: c.conference || "",
      courtroom: c.courtroom || c.hearing_floor || "",
      district: c.district || "",
      charge_level: c.charge_level || "",
      charges_count: Array.isArray(c.charges) ? c.charges.length : 0,
      attorney_name: c.attorney_name || "",
      prosecuting_agency: c.prosecuting_agency?.replace(/^Prosecuting Agency:\s*/i, "") || "",
      otn: (c.otn || "").replace(/^OTN:\s*/i, ""),
      lea: (c.lea || "").replace(/^LEA:\s*/i, ""),
    }));

    const header = Object.keys(rows[0]);
    const csv = [header.join(",")]
      .concat(
        rows.map((r) => header.map((h) => csvEscape(r[h])).join(","))
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `calendar_export_${date || "results"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dacx-toolbar" role="region" aria-label="Results toolbar">
      <div className="dacx-toolbar__left">
        <div className="dacx-summary">
          <strong>Results</strong>
          <span className="dacx-summary__count">— {total} {total === 1 ? "result" : "results"}</span>
        </div>
        <div className="dacx-chips" aria-label="Active filters">
          {chips.map((c, i) => (
            <span key={i} className="dacx-chip" title={`${c.label}: ${c.value}`}>
              <span className="dacx-chip__label">{c.label}</span>
              <span className="dacx-chip__value">{c.value}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="dacx-toolbar__right">
        <div className="dacx-segmented" role="tablist" aria-label="View toggle">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "cards"}
            className={`dacx-segmented__btn ${activeTab === "cards" ? "is-active" : ""}`}
            onClick={() => setActiveTab("cards")}
          >
            Card View
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "table"}
            className={`dacx-segmented__btn ${activeTab === "table" ? "is-active" : ""}`}
            onClick={() => setActiveTab("table")}
          >
            Table View
          </button>
        </div>

        <button
          type="button"
          className={`dacx-button dacx-button--secondary ${expandAll ? "is-on" : ""}`}
          aria-pressed={expandAll}
          onClick={() => setExpandAll((p) => !p)}
          title={expandAll ? "Collapse all case details" : "Expand all case details"}
        >
          <ChevronIcon dir={expandAll ? "up" : "down"} />
          {expandAll ? "Collapse all" : "Expand all"}
        </button>

        <button type="button" className="dacx-button dacx-button--ghost" onClick={handlePrint}>
          Print
        </button>
        <button type="button" className="dacx-button dacx-button--ghost" onClick={handleExport} disabled={!results?.cases?.length}>
          Export CSV
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onClear }) {
  return (
    <div className="dacx-emptystate" role="status">
      <div className="dacx-emptystate__icon" aria-hidden>
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <div className="dacx-emptystate__title">No cases for these filters</div>
      <div className="dacx-emptystate__desc">Try adjusting your filters or clear them to see all available results.</div>
      <button className="dacx-button dacx-button--primary" onClick={onClear}>Clear filters</button>
    </div>
  );
}

function ResultsSkeleton({ view }) {
  if (view === "table") {
    return (
      <div className="dacx-table-skeleton">
        <div className="dacx-skel-row" />
        <div className="dacx-skel-row" />
        <div className="dacx-skel-row" />
      </div>
    );
  }
  return (
    <div className="dacx-card-skeletons">
      <div className="dacx-skel-card" />
      <div className="dacx-skel-card" />
      <div className="dacx-skel-card" />
    </div>
  );
}

function FilterSkeleton() {
  return (
    <div className="dacx-skeleton-form">
      <div className="dacx-skeleton-line" />
      <div className="dacx-skeleton-line" />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="dacx-field">
      <div className="dacx-field__label">
        {label}
        {hint && <span className="dacx-field__hint">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Badge({ children, variant = "neutral", title }) {
  return (
    <span className={`dacx-badge dacx-badge--${variant}`} title={title}>
      {children}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="dacx-stat">
      <div className="dacx-stat__value">{value}</div>
      <div className="dacx-stat__label">{label}</div>
    </div>
  );
}

function ChevronIcon({ dir = "down" }) {
  const rotation = { down: 0, up: 180, left: 90, right: 270 }[dir] || 0;
  return (
    <svg className="dacx-ico" style={{ transform: `rotate(${rotation}deg)` }} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ------------- Shared utilities ------------- */

const formatCaseNo = (v) => (v || "").replace(/^Case\s*#\s*/i, "");
const norm = (v, re) => (v || "").replace(re, "");
const csvEscape = (value) => {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};

function getHearingBadge(hearing_type) {
  const text = hearing_type || "Hearing";
  const isVirtual = (hearing_type || "").toLowerCase().includes("virtual");
  return isVirtual ? (
    <Badge variant="info">Virtual</Badge>
  ) : (
    <Badge variant="warning">{text}</Badge>
  );
}

/* ---------------- Details panel ---------------- */

function RowDetails({ caseItem }) {
  const parties = caseItem.attorneys_or_interested_parties || {};
  const charges = Array.isArray(caseItem.charges) ? caseItem.charges : [];

  return (
    <div className="dacx-details-panel" role="region" aria-label="Case details">
      <div className="dacx-details-grid">
        {/* Hearing */}
        <Section icon="hearing" title="Hearing">
          <KV label="Type" value={caseItem.hearing_type || "—"} />
          <KV label="Conference" value={caseItem.conference || "—"} />
          <KV label="Floor/Room" value={caseItem.hearing_floor || caseItem.courtroom || "—"} />
          {caseItem.warrant_issued ? <KV label="Warrant" value={caseItem.warrant_issued} /> : null}
        </Section>

        {/* Parties */}
        <Section icon="parties" title="Parties">
          <KV label="Plaintiff" value={caseItem.plaintiff || "—"} />
          <KV label="Defendant" value={caseItem.defendant || "—"} />
          {caseItem.dob ? <KV label="DOB" value={norm(caseItem.dob, /^DOB:\s*/i)} /> : null}
        </Section>

        {/* Identifiers */}
        <Section icon="ids" title="Identifiers">
          <KV label="Case #" value={formatCaseNo(caseItem.case_number)} />
          <KV label="OTN" value={norm(caseItem.otn, /^OTN:\s*/i) || "—"} />
          <KV label="Citation" value={norm(caseItem.citation_number, /^Citation:\s*#?\s*/i) || "—"} />
          <KV label="LEA" value={norm(caseItem.lea, /^LEA:\s*/i) || "—"} />
          <KV label="Sheriff" value={norm(caseItem.sheriff, /^Sheriff:\s*/i) || "—"} />
        </Section>

        {/* Prosecution */}
        <Section icon="prosecution" title="Prosecution">
          <KV label="Agency" value={norm(caseItem.prosecuting_agency, /^Prosecuting Agency:\s*/i) || "—"} />
          <KV label="Prosecutor" value={caseItem.attorney_name || "—"} />
        </Section>
      </div>

      {/* Attorneys / Interested Parties */}
      {Object.keys(parties).length ? (
        <div className="dacx-details-block">
          <div className="dacx-details-heading">Attorneys / Interested Parties</div>
          {(() => {
            const ROLE_LABELS = {
              "PLA ATTY": "Plaintiff Attorney",
              "DEF ATTY": "Defense Attorney",
              "PET ATTY": "Petitioner Attorney",
              "RES ATTY": "Respondent Attorney",
              "GAL ATTY": "Guardian ad Litem",
              "MIN ATTY": "Minor's Counsel",
              "COC ATTY": "Counsel of Record",
              "INT ATTY": "Interested Party",
            };
            const ROLE_ORDER = ["DEF ATTY", "PLA ATTY", "PET ATTY", "RES ATTY", "GAL ATTY", "MIN ATTY", "COC ATTY", "INT ATTY"];
            const known = ROLE_ORDER.flatMap((role) => (parties[role] || []).map((name) => ({ role, name })));
            const unknownRoles = Object.keys(parties)
              .filter((r) => !ROLE_ORDER.includes(r))
              .flatMap((role) => (parties[role] || []).map((name) => ({ role, name })));
            const entries = [...known, ...unknownRoles];
            return entries.length ? (
              <ul className="dacx-party-list">
                {entries.map((e, i) => (
                  <li key={`${e.role}-${i}`} className="dacx-party-list__item">
                    <span className="dacx-role-prefix">{e.role}:</span>
                    <span className="dacx-party-name">{e.name}</span>
                    <span className="dacx-role-hint">({ROLE_LABELS[e.role] || "Party / Counsel"})</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="dacx-muted">No attorneys or interested parties listed.</div>
            );
          })()}
        </div>
      ) : null}

      {/* Charges */}
      <div className="dacx-details-block">
        <div className="dacx-details-heading">Charges</div>
        {!charges.length ? (
          <div className="dacx-muted">No charges listed.</div>
        ) : (
          <ul className="dacx-charge-list">
            {charges.map((chg, idx) => (
              <li key={idx} className="dacx-charge-item">
                <div className="dacx-charge-head">
                  <Badge variant="case">{(chg.level || "Charge").trim()}</Badge>
                  {chg.date ? <span className="dacx-charge-date">{chg.date}</span> : null}
                </div>
                <div className="dacx-charge-desc">{chg.description}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div className="dacx-details-heading">
        <span className={`dacx-ico-wrap dacx-ico-${icon}`} aria-hidden />
        {title}
      </div>
      <div className="dacx-details-body">{children}</div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="dacx-kv">
      <div className="dacx-kv__label">{label}</div>
      <div className="dacx-kv__value">{value}</div>
    </div>
  );
}

/* ---------------- Cards View ---------------- */

function ResultsCards({ data, expandAll }) {
  const [open, setOpen] = useState(() => new Set());

  useEffect(() => {
    if (!data?.cases) return;
    if (expandAll) {
      setOpen(new Set(data.cases.map((_, i) => i)));
    } else {
      setOpen(new Set());
    }
  }, [expandAll, data]);

  const cases = data?.cases || [];
  if (!cases.length) return null;

  const total = typeof data?.results_count === "number" ? data.results_count : cases.length;
  const virtualCount = cases.filter((c) => (c.hearing_type || "").toLowerCase().includes("virtual")).length;
  const inPersonCount = cases.filter((c) => (c.hearing_type || "").toLowerCase().includes("in person")).length;

  const toggle = (idx) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  return (
    <div>
      <div className="dacx-stats">
        <Stat label="Virtual" value={virtualCount} />
        <Stat label="In Person" value={inPersonCount} />
        <Stat label="Other/Unk" value={Math.max(total - virtualCount - inPersonCount, 0)} />
      </div>

      {cases.map((c, i) => {
        const isOpen = open.has(i);
        const caseNo = formatCaseNo(c.case_number) || "—";

        return (
          <div className="dacx-card" key={i}>
            <div className="dacx-card__header">
              <div className="dacx-card__title">
                <span className="dacx-case-number" title="Case number">{caseNo}</span>
                <span className="dacx-defendant" title="Defendant">{c.defendant || "—"}</span>
                {c.charge_level ? <span className="dacx-level">{c.charge_level}</span> : null}
              </div>
              <button
                type="button"
                className="dacx-button dacx-button--ghost dacx-button--icon"
                aria-expanded={isOpen}
                aria-controls={`card-details-${i}`}
                onClick={() => toggle(i)}
                title={isOpen ? "Hide details" : "Show details"}
              >
                <ChevronIcon dir={isOpen ? "up" : "down"} />
              </button>
            </div>

            <div className="dacx-card__meta">
              {getHearingBadge(c.hearing_type)}
              {c.conference ? <span className="dacx-badge dacx-badge--neutral">{c.conference}</span> : null}
              <span>{c.time || "—"}</span>
              <span>{c.date || "—"}</span>
              {(c.courtroom || c.hearing_floor) && (
                <span title="Room">{c.courtroom || c.hearing_floor}</span>
              )}
              {c.district ? <span title="District">{c.district}</span> : null}
            </div>

            <div className="dacx-card__row">
              <div className="dacx-party">
                <div className="dacx-kvline">
                  <span className="dacx-kvline__label">Pltf</span>
                  <span className="dacx-kvline__value">{c.plaintiff || "—"}</span>
                </div>
                {c.dob ? <div className="dacx-muted">{c.dob.replace(/^DOB:\s*/i, "")}</div> : null}
              </div>

              <div className="dacx-ids">
                <div>
                  <span className="dacx-id-label">OTN</span>
                  <span className="dacx-id-value">{norm(c.otn, /^OTN:\s*/i) || "—"}</span>
                </div>
                <div>
                  <span className="dacx-id-label">LEA</span>
                  <span className="dacx-id-value">{norm(c.lea, /^LEA:\s*/i) || "—"}</span>
                </div>
              </div>

              <div className="dacx-charges">
                {Array.isArray(c.charges) && c.charges.length > 0 ? (
                  <>
                    <span className="dacx-chip">Charges <span className="dacx-chip__value">{c.charges.length}</span></span>
                    {c.charges[0]?.description ? (
                      <div className="dacx-charge-preview" title={c.charges[0].description}>
                        {c.charges[0].description}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <span className="dacx-muted">No charges listed</span>
                )}
              </div>
            </div>

            <div
              id={`card-details-${i}`}
              className={`dacx-details-wrap ${isOpen ? "is-open" : ""}`}
            >
              {isOpen && <RowDetails caseItem={c} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Table View ---------------- */

function ResultsTable({ data, expandAll }) {
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (!data?.cases) return;
    if (expandAll) {
      setExpanded(new Set(data.cases.map((_, i) => i)));
    } else {
      setExpanded(new Set());
    }
  }, [expandAll, data]);

  const cases = data?.cases || [];
  if (!cases.length) return null;

  const total = typeof data?.results_count === "number" ? data.results_count : cases.length;
  const virtualCount = cases.filter((c) => (c.hearing_type || "").toLowerCase().includes("virtual")).length;
  const inPersonCount = cases.filter((c) => (c.hearing_type || "").toLowerCase().includes("in person")).length;

  const toggleRow = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const COL_COUNT = 7;

  return (
    <div>
      <div className="dacx-stats">
        <Stat label="Virtual" value={virtualCount} />
        <Stat label="In Person" value={inPersonCount} />
        <Stat label="Other/Unk" value={Math.max(total - virtualCount - inPersonCount, 0)} />
      </div>

      <div className="dacx-table-wrap">
        <table className="dacx-table">
          <thead>
            <tr>
              <th style={{ width: 56 }} aria-label="Expand" />
              <th style={{ width: 100 }}>Time</th>
              <th>Case</th>
              <th>Parties</th>
              <th style={{ width: 120 }}>IDs</th>
              <th>Charges</th>
              <th style={{ width: 160 }}>Location</th>
              <th style={{ width: 160 }}>Prosecution</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c, i) => {
              const isOpen = expanded.has(i);
              const caseNo = formatCaseNo(c.case_number) || "—";

              return (
                <Fragment key={i}>
                  <tr className="dacx-row">
                    <td className="dacx-cell--icon">
                      <button
                        type="button"
                        className="dacx-button dacx-button--ghost dacx-button--icon"
                        aria-expanded={isOpen}
                        aria-controls={`row-details-${i}`}
                        onClick={() => toggleRow(i)}
                        title={isOpen ? "Hide details" : "Show details"}
                      >
                        <ChevronIcon dir={isOpen ? "up" : "down"} />
                      </button>
                    </td>

                    {/* Time */}
                    <td>
                      <div className="dacx-time">{c.time || "—"}</div>
                      <div className="dacx-date">{c.date || "—"}</div>
                    </td>

                    {/* Case */}
                    <td>
                      <div className="dacx-case-id">
                        <span className="dacx-case-number">{caseNo}</span>
                        {c.charge_level ? <span className="dacx-level">{c.charge_level}</span> : null}
                      </div>
                      <div className="dacx-line">
                        {getHearingBadge(c.hearing_type)}
                        {c.conference ? <span className="dacx-badge dacx-badge--neutral">{c.conference}</span> : null}
                      </div>
                    </td>

                    {/* Parties */}
                    <td>
                      <div className="dacx-party">
                        <div className="dacx-defendant">{c.defendant || "—"}</div>
                        <div className="dacx-kvline">
                          <span className="dacx-kvline__label">Pltf</span>
                          <span className="dacx-kvline__value">{c.plaintiff || "—"}</span>
                        </div>
                        {c.dob ? <div className="dacx-muted">{c.dob.replace(/^DOB:\s*/i, "")}</div> : null}
                      </div>
                    </td>

                    {/* IDs */}
                    <td>
                      <div className="dacx-ids">
                        <div>
                          <span className="dacx-id-label">OTN</span>
                          <span className="dacx-id-value">{norm(c.otn, /^OTN:\s*/i) || "—"}</span>
                        </div>
                        <div>
                          <span className="dacx-id-label">LEA</span>
                          <span className="dacx-id-value">{norm(c.lea, /^LEA:\s*/i) || "—"}</span>
                        </div>
                      </div>
                    </td>

                    {/* Charges */}
                    <td>
                      {Array.isArray(c.charges) && c.charges.length > 0 ? (
                        <>
                          <span className="dacx-chip">
                            Charges <span className="dacx-chip__value">{c.charges.length}</span>
                          </span>
                          {c.charges[0]?.description ? (
                            <div className="dacx-charge-preview" title={c.charges[0].description}>
                              {c.charges[0].description}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <span className="dacx-muted">No charges listed</span>
                      )}
                    </td>

                    {/* Location */}
                    <td>
                      <div className="dacx-location">
                        <div>{c.courtroom || c.hearing_floor || "—"}</div>
                        {c.hearing_floor && c.courtroom && c.hearing_floor !== c.courtroom ? (
                          <div className="dacx-muted">{c.hearing_floor}</div>
                        ) : null}
                        {c.district ? <div className="dacx-muted">{c.district}</div> : null}
                      </div>
                    </td>

                    {/* Prosecution */}
                    <td>
                      <div className="dacx-prosecution">
                        {c.attorney_name ? <div><strong>{c.attorney_name}</strong></div> : null}
                        {c.prosecuting_agency ? (
                          <div className="dacx-muted">{c.prosecuting_agency.replace(/^Prosecuting Agency:\s*/i, "")}</div>
                        ) : null}
                      </div>
                    </td>
                  </tr>

                  {/* Details row */}
                  <tr className={`dacx-details-row ${isOpen ? "is-open" : ""}`}>
                    <td colSpan={COL_COUNT + 1} className="dacx-details-cell" id={`row-details-${i}`}>
                      {isOpen && <RowDetails caseItem={c} />}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
